import { useState } from "react"

import { Alert, Divider, Flex } from "@/atoms"
import { getDetailMessage } from "@/helpers/error.helper"
import { useTranslation } from "@/hooks/useTranslation"
import { EyeOutlined, FileProtectOutlined, StopOutlined } from "@/icons"
import { Form } from "@/organisms/Form"
import { SpecificResources } from "@/organisms/Form/components/ResourcesSectionForm/components/SpecificResources"
import {
  getAiPolicyFormValues,
  hasCondition
} from "@/pages/GovernancePage/applications/ai-policies/useLiveAiPolicies.selector"
import { createScopeRecord } from "@/pages/GovernancePage/applications/policies/createScopeRecord"
import { getAssetsScope } from "@/pages/GovernancePage/applications/policies/getDemoData"
import { useAiPolicies } from "@/pages/GovernancePage/applications/policies/useAiPolicies"
import { LiveScopeResources } from "@/pages/GovernancePage/components/organisms/LiveScopeResources"
import {
  ENFORCEMENT_SECTION_ID,
  EnforcementSection
} from "../EnforcementSection"
import { RuleField } from "../RuleField"
import { RuleTitle } from "../RuleTitle"
import { SummaryPolicy } from "../SummaryPolicy"
import { getInitialState } from "./helpers"

import styles from "./AiPolicyForm.module.scss"

import type {
  AiPolicyFormProps,
  AiPolicyFormValues
} from "./AiPolicyForm.types"

const FAKE_SAVE_DELAY_MS = 1200
// Lets the redirect happen first, so the toast shows on the destination page.
const SUCCESS_TOAST_DELAY_MS = 400

export const AiPolicyForm = (props: AiPolicyFormProps) => {
  const {
    update,
    create,
    saveAgentPolicy,
    features,
    live,
    RuleField: CustomRuleField
  } = useAiPolicies()
  const Rule = CustomRuleField ?? RuleField
  const { t } = useTranslation()
  const { t: tForm } = useTranslation("translation", {
    keyPrefix: "governance.ai.form"
  })
  const { form, message } = Form.useForm()
  const { record, mode, redirectUrl, setUpdating, agent } = props
  const types = features.allowRules
    ? (["allow", "notAllow"] as const)
    : (["notAllow"] as const)
  const [error, setError] = useState<string>()

  const save = (values: AiPolicyFormValues) => {
    if (mode === "drawer" && agent) return saveAgentPolicy(agent, values)
    if (record) return update(`${record.id}`, values)

    return create(createScopeRecord(values, `policy-${Date.now()}`), values)
  }

  const saveLive = async (values: AiPolicyFormValues) => {
    if (!live) return

    setError(undefined)
    setUpdating?.(true)

    try {
      await live.save(values, record)
      message.onSuccess(values.name_details, {})
    } catch (err) {
      setError(getDetailMessage(err) || t("error.serverError"))

      throw err
    } finally {
      setUpdating?.(false)
    }
  }

  return (
    <Form<AiPolicyFormValues>
      initialValues={
        live
          ? {
              ...getInitialState(record),
              ...getAiPolicyFormValues(record?.live)
            }
          : getInitialState(record)
      }
      navigateOnFinished={redirectUrl}
      onValuesChange={(changed: Partial<AiPolicyFormValues>) => {
        // A list's own rules run again only when an item is added or
        // removed: a shown rule error goes once its rules change
        types.forEach((type) => {
          if (type in changed && form.getFieldError([type]).length)
            form.validateFields([type]).catch(() => undefined)
        })
      }}
      onFinish={(values) => {
        if (live) return saveLive(values)

        setUpdating?.(true)

        return new Promise((resolve) => {
          save(values)
          setTimeout(() => resolve(true), FAKE_SAVE_DELAY_MS)
        })
          .then(() => {
            setTimeout(
              () => message.onSuccess(values.name_details, {}),
              SUCCESS_TOAST_DELAY_MS
            )
          })
          .finally(() => setUpdating?.(false))
      }}
    >
      {error && <Alert showIcon type="error" title={error} />}
      {mode !== "drawer" && (
        <Form.Section.Details
          prefix={
            record && (
              <>
                <SummaryPolicy record={record} />
                <Divider className={styles.divider16} />
              </>
            )
          }
          title="AI Policy Details"
          label="AI Policy Name"
        />
      )}
      {(!record || features.editableScope) && (
        <Form.Section.Resources
          form={form}
          Icon={EyeOutlined}
          keyPrefix="governance.ai.form.resources"
        >
          {live ? (
            <LiveScopeResources form={form} tForm={tForm} />
          ) : (
            <SpecificResources
              form={form}
              options={getAssetsScope()}
              tForm={tForm}
            />
          )}
        </Form.Section.Resources>
      )}
      <Form.Section.Template
        id="rules"
        title="Rules"
        Icon={FileProtectOutlined}
      >
        <Flex vertical gap={32} className={styles.rulesSection}>
          {types.map((type) => (
            <Flex vertical gap={14} key={type}>
              <RuleTitle type={type} />
              <Form.List
                name={type}
                rules={
                  live && [
                    {
                      validator: async (_, groups) => {
                        if (!hasCondition(groups))
                          throw new Error(
                            t("governance.ai.live.form.rule.required")
                          )
                      }
                    }
                  ]
                }
              >
                {(fields, listActions, { errors }) => (
                  <>
                    {fields.length === 0 && (
                      <Rule name={0} type={type} listActions={listActions} />
                    )}
                    {fields.map(({ key, name }) => (
                      <Rule
                        key={key}
                        name={name}
                        type={type}
                        listActions={listActions}
                      />
                    ))}
                    {live && <Form.ErrorList errors={errors} />}
                  </>
                )}
              </Form.List>
            </Flex>
          ))}
        </Flex>
      </Form.Section.Template>
      {features.enforcement && (
        <Form.Section.Template
          id={ENFORCEMENT_SECTION_ID}
          title={t("governance.ai.live.form.enforcement.title")}
          Icon={StopOutlined}
        >
          <div className={styles.rulesSection}>
            <EnforcementSection />
          </div>
        </Form.Section.Template>
      )}
    </Form>
  )
}
